/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useState, useMemo } from 'react';
import { KTSVG } from '../../../../helpers';
import { toast } from 'react-toastify';
import { get_employee_shift_hours_report } from '../api/timeAtWorkApi';
import { useQuery } from 'react-query';
import moment from 'moment';
import "../../../../assets/keenicons/duotone/style.css";

// Define interface for a single shift record from the API data array
interface ShiftRecord {
  date: string; // YYYY-MM-DD format
  total_hours: number;
  recent_attendance?: { // Optional, as it might not always be present or be a checkout
    title: string;
    attendance_datetime: string; // YYYY-MM-DD HH:mm:ss format
  } | null;
}

// Define an interface for the API response itself
interface ShiftReportApiResponse {
  status: boolean;
  message: string;
  data: ShiftRecord[] | null; // Data can be null or array based on status/message
}

// Interface for processed data for the chart
interface ChartDayData {
  date: string; // YYYY-MM-DD
  day: string; // Mon, Tue, etc.
  total_hours: number;
  display_time: string; // e.g., "2h 30m"
}

type Props = {
  className: string;
};

const TimeAtWorkWidget: React.FC<Props> = ({ className }) => {
  const currentUser = localStorage.getItem('auth_user_id');
  const userId = currentUser ? JSON.parse(currentUser) : null;
  const currentUserData = localStorage.getItem('kt-auth-react-v');
  const currentData = currentUserData ? JSON.parse(currentUserData) : null;
  const userInitial = currentData?.user?.full_name?.[0] || '?'; // Get first letter or '?'

  // Calculate dates for the last 7 days including today
  const today = moment().format('YYYY-MM-DD');
  // Subtract 6 days to include today, covering a total of 7 days
  const sevenDaysAgoIncludingToday = moment().subtract(6, 'days').format('YYYY-MM-DD');

  // State for the API request payload
  const [filterPayload] = useState<any>(() => {
    const initialPayload: any = {
      from_date: sevenDaysAgoIncludingToday,
      to_date: today,
      date_flag: "specific",
      user_flag: "specific"
    };

    if (userId) {
      initialPayload.user_id = userId;
    } else {
      console.warn('User ID not found in localStorage. Filter may not apply correctly.');
    }

    return initialPayload;
  });

  // State to hold the full report data fetched from the API
  const [fullReportData, setFullReportData] = useState<ShiftRecord[]>([]);

  // Fetch Shift Report Data using React Query
  const {
    isLoading: isReportLoading,
    isFetching,
  } = useQuery<ShiftReportApiResponse>(
    ['shiftReportData', filterPayload],
    () => get_employee_shift_hours_report(filterPayload),
    {
      onSuccess: (apiResponse) => {
        if (apiResponse?.status && Array.isArray(apiResponse.data)) {
          setFullReportData(apiResponse.data.map(record => ({
            ...record,
            total_hours: record.total_hours || 0
          })));
        } else {
          setFullReportData([]);
          if (apiResponse && !apiResponse.status && apiResponse.message) {
             toast.error(apiResponse.message, { position: 'top-right', autoClose: 4000 });
          } else if (!apiResponse?.data && Object.keys(filterPayload).length > 0) {
              console.warn('No data returned for the filter criteria.');
          }
        }
      },
      onError: (error: any) => {
        const errorMessage = error?.response?.data?.message || 'Failed to fetch shift hours report.';
        toast.error(errorMessage, { position: 'top-right', autoClose: 4000 });
        setFullReportData([]);
      },
      enabled: !!userId && Object.keys(filterPayload).length > 0,
      refetchOnWindowFocus: false,
      keepPreviousData: true,
    }
  );

  // --- Data Processing using useMemo ---

  const lastCheckoutInfo:any= useMemo(() => {
    if (!fullReportData || fullReportData.length === 0) {
      return null;
    }

    let latestCheckout: ShiftRecord | null = null;
    let latestDateTime: moment.Moment | null = null;

    fullReportData.forEach(record => {
      if (record.recent_attendance && record.recent_attendance.title === 'Check-out') {
        const attendanceTime = moment(record.recent_attendance.attendance_datetime);
        if (!latestDateTime || attendanceTime.isAfter(latestDateTime)) {
          latestDateTime = attendanceTime;
          latestCheckout = record;
        }
      }
    });

    return latestCheckout;

  }, [fullReportData]);

  const { processedChartData, maxHours } = useMemo(() => {
    const dataMap: { [date: string]: ShiftRecord } = {};
    if (fullReportData) {
      fullReportData.forEach(record => {
        dataMap[record.date] = record;
      });
    }

    const chartData: ChartDayData[] = [];
    let currentMaxHours = 0;

    for (let i = 6; i >= 0; i--) {
      const date = moment().subtract(i, 'days').format('YYYY-MM-DD');
      const dayName = moment(date).format('ddd');
      const record = dataMap[date];

      const totalHours = record?.total_hours || 0;

      const hours = Math.floor(totalHours);
      const minutes = Math.floor((totalHours - hours) * 60);
      const displayTime = `${hours}h ${minutes}m`;

      chartData.push({
        date,
        day: dayName,
        total_hours: totalHours,
        display_time: displayTime,
      });

      if (totalHours > currentMaxHours) {
        currentMaxHours = totalHours;
      }
    }

    return { processedChartData: chartData, maxHours: currentMaxHours };

  }, [fullReportData]);

  const todayTime = useMemo(() => {
    const todayRecord = fullReportData.find(record => record.date === today);
    const totalHours = todayRecord?.total_hours || 0;
    const hours = Math.floor(totalHours);
    const minutes = Math.floor((totalHours - hours) * 60);
    return `${hours}h ${minutes}m`;
  }, [fullReportData, today]);

  const thisWeekTime = useMemo(() => {
    const totalHoursSum = fullReportData.reduce((sum, record) => sum + (record?.total_hours || 0), 0);
    const hours = Math.floor(totalHoursSum);
    const minutes = Math.floor((totalHoursSum - hours) * 60);
    return `${hours}h ${minutes}m`;
  }, [fullReportData]);

  const formatAttendanceDateTime = (datetimeString: string) => {
     const momentObj = moment(datetimeString);
     // Adding timezone logic might require more data from the API
     return momentObj.format('MMM Do [at] hh:mm A'); // Example: Jun 9th at 05:18 PM
  };


  // --- Render Logic ---

  return (
    // Add the scoping class here
    <div className={`card ${className} widget-time-at-work`}>
      {/* begin::Header */}
      <div className='card-header border-0 pt-5'>
        <h3 className='card-title align-items-start flex-column'>
          <span className='card-label fw-bold fs-3 mb-1'>
            <span className='me-2'>
              <KTSVG path='/media/icons/duotune/electronics/elc002.svg' className='svg-icon-3' />{' '}
            </span>
           My Daily Time Sheet in this week
          </span>
        </h3>

        {/* begin::Toolbar */}
        <div className='card-toolbar'>
          {isReportLoading || isFetching ? (
            <div className='spinner-border spinner-border-sm' role='status'>
              <span className='visually-hidden'>Loading...</span>
            </div>
          ) : (
             null
          )}
        </div>
        {/* end::Toolbar */}
      </div>
      {/* end::Header */}

      {/* begin::Body */}
      <div className='card-body d-flex flex-column p-5'>
        {/* User Info & Last Checkout */}
        <div className='d-flex align-items-center mb-5'>
          {/* Profile Icon (User Initial) - Apply new class */}
          <div className='mx-3 symbol symbol-circle bg-dark text-white d-flex align-items-center justify-content-center widget-profile-icon'>
            <span className='fs-3 fw-bold'>
              {userInitial}
            </span>
          </div>
          <div className="d-flex flex-column flex-grow-1">
            <div className="fs-4 text-gray-800">
              <div className="fw-bold">Recent Week</div>
              <div className="fw-bold">
                {lastCheckoutInfo ? 'Last Check Out' : 'No recent Check Out'}
              </div>
            </div>

            <div className="text-muted fs-7">
              {lastCheckoutInfo
                ? `On ${formatAttendanceDateTime(lastCheckoutInfo.recent_attendance!.attendance_datetime)}`
                : 'Looking for your last checkout...'}
            </div>
          </div>

        </div>

        {/* Today's Time - Apply new class for background */}
        <div className='d-flex align-items-center rounded p-5 mb-6 widget-light-bg'>
          <div className='d-flex flex-grow-1 align-items-center'>
            <span className='fw-bold fs-2qx text-gray-800 me-3'>{todayTime}</span>
            <span className='text-gray-500 fw-semibold fs-7'>Today</span>
          </div>
        </div>

        {/* This Week's Time - Apply new class for background */}
        <div className='d-flex justify-content-between align-items-center mb-4'>
          <div className='d-flex flex-column'>
            <div className='fw-bold text-gray-800 mb-1'>This Week</div>
            <div className='text-muted fs-7'>{`${moment(sevenDaysAgoIncludingToday).format('MMM Do')} - ${moment(today).format('MMM Do')}`}</div>
          </div>
           {/* Apply new class for background */}
          <div className='d-flex align-items-center rounded py-1 px-3 widget-light-bg'>
            <KTSVG path='/media/icons/duotune/electronics/elc002.svg' className='svg-icon-3 me-1 text-gray-600' />
            <span className='fw-bold text-gray-600'>{thisWeekTime}</span>
          </div>
        </div>

        {/* Daily Chart - Apply new class for container */}
        <div className='d-flex justify-content-between pt-4 border-top border-dashed widget-chart-container'>
            {isReportLoading || isFetching ? (
                 // Apply new class for message container
                 <div className='d-flex w-100 justify-content-center align-items-center widget-chart-message'>
                     <span className='text-muted'>Loading chart data...</span>
                 </div>
            ) : processedChartData.length > 0 ? (
                 processedChartData.map((dayData) => {
                   const baseHeightPx = 5;
                   const maxBarHeightPx = 80; // Max visual height above base

                   let barHeight = 0;
                   if (dayData.total_hours > 0) {
                      if (maxHours > 0) {
                         // Scale height between baseHeightPx and (baseHeightPx + maxBarHeightPx)
                         barHeight = baseHeightPx + (dayData.total_hours / maxHours) * maxBarHeightPx;
                      } else {
                          barHeight = baseHeightPx; // Fallback if maxHours is 0 but total_hours > 0 (shouldn't happen)
                      }
                   }
                   // Cap height just in case
                   barHeight = Math.min(barHeight, baseHeightPx + maxBarHeightPx);


                   const barColorClass = dayData.total_hours > 0.1 ? 'bg-primary' : 'bg-secondary';

                   return (
                     <div key={dayData.date} className='d-flex flex-column flex-center'
                          // Keep dynamic width inline
                          style={{ width: `${100 / processedChartData.length}%` }}>
                         {/* Bar - Keep dynamic height inline */}
                         <div className={`w-15px rounded-pill ${barColorClass} flex-grow-0`} // flex-grow-0 important so height is respected
                              style={{ height: `${barHeight}px` }}>
                         </div>
                         {/* Day Name */}
                         <span className='text-muted fs-7 mt-2'>{dayData.day}</span>
                         {/* Time */}
                         <span className='fs-8 fw-bold text-gray-800'>{dayData.display_time}</span>
                     </div>
                   );
                 })
            ) : (
                 // Apply new class for message container
                 <div className='d-flex w-100 justify-content-center align-items-center widget-chart-message'>
                     <span className='text-muted'>No shift data available for the last 7 days.</span>
                 </div>
            )}
        </div>
      </div>
      {/* end::Body */}
    </div>
  );
};

export { TimeAtWorkWidget };